<template>
  <v-autocomplete
    v-model="selection"
    class="my-1 combobox-role"
    color="primary"
    :multiple="multiple"
    density="compact"
    variant="outlined"
    hide-details
    clearable
    :aria-label="filterNode.name"
    :custom-filter="customFilter"
    :menu-props="{ closeOnContentClick: true }"
    :items="filterNode.values"
    :placeholder="filterNode.name"
    return-object
    item-value="term"
    item-title="name"
    :disabled="disabled"
    @update:model-value="
      () => {
        $emit('update')
      }
    "
    @update:focused="
      (focus) => {
        hasFocus = focus
      }
    "
  >
    <template v-if="multiple" #selection="{ index }">
      <span v-if="index === 0 && !hasFocus">{{ filterNode.name }}</span>
    </template>
    <template v-else #selection="{ item }">
      <span v-if="!hasFocus">
        {{ item.name + (item.count ? ' (' + item.count + ')' : '') }}
      </span>
    </template>
    <template #item="{ item, props }">
      <v-list-item v-bind="props" title="">
        <v-list-item-title>
          {{ item.name + (item.count ? ' (' + item.count + ')' : '') }}
        </v-list-item-title>
      </v-list-item>
    </template>
  </v-autocomplete>
  <template v-if="multiple && Array.isArray(selection) && selection.length > 0">
    <v-chip
      v-for="filter in selection"
      :key="filter.term"
      class="mr-1 mb-1"
      :closable="!disabled"
      @click:close="onRemove(filter)"
    >
      {{ filter.name }}
    </v-chip>
  </template>
</template>

<script setup lang="ts">
import { isFilterValue, isFilterValueArray } from '@/components/filter/helpers'
import FilterEvent from '@/modules/tracking/events/filter'
import { trackEvent } from '@/modules/tracking/useTracking'
import { InternalItem } from '@/types'
import { FilterIdentifier, FilterValue } from '@/types/index'
import { difference } from 'lodash-es'
import { computed, PropType, Ref, ref } from 'vue'

const componentProps = defineProps({
  filterNode: {
    type: Object as PropType<FilterIdentifier>,
    required: true
  },
  getSelectionForFilter: {
    type: Function as PropType<(name: string) => FilterValue[] | undefined>,
    required: true
  },
  updateFilterSelection: {
    type: Function as PropType<(name: string, value: FilterValue[] | undefined) => void>,
    required: true
  },
  removeItemFromFilterSelection: {
    type: Function as PropType<(name: string, item: FilterValue) => void>,
    required: true
  },
  multiple: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  filterEvent: {
    type: Function as PropType<(label: string) => FilterEvent | undefined>,
    default: undefined
  }
})

const emit = defineEmits<{
  update: []
}>()

const hasFocus = ref(false)
let selection: Ref<FilterValue | FilterValue[] | undefined>

if (componentProps.multiple) {
  selection = computed({
    get: () => componentProps.getSelectionForFilter(componentProps.filterNode.name),
    set: (value) => {
      trackFilterChange(value)
      componentProps.updateFilterSelection(componentProps.filterNode.name, value)
    }
  })
} else {
  selection = computed({
    get: () => componentProps.getSelectionForFilter(componentProps.filterNode.name)?.[0],
    set: (value) => {
      trackFilterChange(value)
      componentProps.updateFilterSelection(componentProps.filterNode.name, value ? [value] : [])
    }
  })
}

const trackFilterChange = (value: FilterValue | FilterValue[] | undefined) => {
  let oldValues: FilterValue[] = []
  let newValues: FilterValue[] = []

  if (isFilterValue(value)) {
    newValues = [value]
  } else if (isFilterValueArray(value)) {
    newValues = value
  }

  if (selection.value == undefined) {
    oldValues = []
  } else if (isFilterValue(selection.value)) {
    oldValues = [selection.value]
  } else if (isFilterValueArray(selection.value)) {
    oldValues = selection.value
  }

  const calculatedDifference = difference(newValues, oldValues)

  const first = calculatedDifference[0]
  if (!first || !componentProps.filterEvent) return
  const event = componentProps.filterEvent(`${componentProps.filterNode.name}:${first.name}`)
  if (!event) return

  if (event.action === 'Filter geändert' && event.label?.startsWith('Hersteller:')) {
    event.action = 'Herstellerfilter geändert'
    event.label = event.label.substring(11)
  }
  trackEvent(event)
}

const customFilter = (_: string, query: string, item?: InternalItem<FilterValue>) => {
  return item?.raw.name.toLowerCase().includes(query.toLocaleLowerCase()) || false
}

const onRemove = (filter: FilterValue) => {
  componentProps.removeItemFromFilterSelection(componentProps.filterNode.name, filter)
  emit('update')
}
</script>
